import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { REDO_MERCHANT_ONBOARDING_URL } from "@redotech/redo-merchant-app-common/config";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import * as linkCss from "@redotech/redo-web/link.module.css";
import { Modal, ModalSize } from "@redotech/redo-web/modal";
import { Spinner, SpinnerStyle } from "@redotech/redo-web/spinner";
import classNames from "classnames";
import { ReactNode, memo, useContext, useEffect, useState } from "react";
import { getCurrentOnboarding, onboardingTc } from "../client/onboarding";
import * as indexCss from "./index.module.css";

const tcUrl = new URL("https://www.getredo.com/terms-conditions");
const setUpOnboardingUrl = new URL(
  "https://df6ln.share.hsforms.com/2HfPRu66ySwWl0ScYAnAl7Q",
);

export function autoOnboardingUrl(token: string, teamId: string) {
  const url = new URL(REDO_MERCHANT_ONBOARDING_URL);
  url.searchParams.set("token", token);
  url.searchParams.set("teamId", teamId);
  return url;
}

export const Onboarding = memo(function Onboarding() {
  const team = useContext(TeamContext);
  const auth = useContext(AuthContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const [onboarded, setOnboarded] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const onboardingFeatureFlag =
    team?.featureFlags?.[FeatureFlagKey.MERCHANT_ONBOARDING];

  const [acceptTermsLoad, doAcceptTerms] = useTriggerLoad(async (signal) => {
    return alertOnFailure("Failed to onboard")(async () => {
      await onboardingTc(client, { signal });
      setOnboarded(true);
      return true;
    });
  });

  const onboardingLoad = useLoad(async (signal) => {
    const data = await getCurrentOnboarding(client, { signal });
    setShowModal(!data.tcConfirmed);
    setOnboarded(data.confirmed);
    return data;
  }, []);

  useEffect(() => {
    if (
      auth &&
      !onboarded &&
      onboardingLoad.value?.tcConfirmed &&
      onboardingFeatureFlag === "guided"
    ) {
      location.href = autoOnboardingUrl(auth.token, auth.teamId).toString();
    }
  }, [auth, onboarded, onboardingLoad, onboardingFeatureFlag]);

  useEffect(() => {
    const isShopify =
      team?.name.toLowerCase().includes("shpfy test") ||
      team?.email.endsWith("@shopify.com");
    if (acceptTermsLoad.value && isShopify) {
      // Shopify doesn't like the onboarding prompt
      setShowModal(false);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptTermsLoad]);

  let title: ReactNode;
  let content: ReactNode;
  let footer: ReactNode;
  if (acceptTermsLoad.pending) {
    title = <>Setting up Redo</>;
    content = (
      <div className={indexCss.spinner}>
        <div className={indexCss.spinnerContainer}>
          <Spinner style={SpinnerStyle.AUTO} />
        </div>
      </div>
    );
    footer = <div />;
  } else if (acceptTermsLoad.value) {
    title = <>Welcome to Redo</>;
    content = (
      <>
        <div className={indexCss.text}>
          To finish setting up Redo, please schedule an appointment with our
          onboarding specialists.
        </div>
        <a
          className={classNames(linkCss.link, indexCss.featuredLink)}
          href={setUpOnboardingUrl.toString()}
          rel="noreferrer"
          target="_blank"
        >
          Schedule an appointment
        </a>
      </>
    );
    footer = (
      <Button
        className={indexCss.button}
        onClick={() => setShowModal(false)}
        theme={ButtonTheme.PRIMARY}
      >
        Continue to Redo
      </Button>
    );
  } else {
    title = <>Welcome to Redo</>;
    content = (
      <>
        <div className={indexCss.text}>
          To continue with installation, please review and accept the terms and
          conditions.
        </div>
        <a
          className={classNames(linkCss.link, indexCss.featuredLink)}
          href={tcUrl.toString()}
          rel="noreferrer"
          target="_blank"
        >
          Read them here
        </a>
      </>
    );
    footer = (
      <Button
        className={indexCss.button}
        onClick={() => doAcceptTerms()}
        theme={ButtonTheme.PRIMARY}
      >
        Accept
      </Button>
    );
  }

  return (
    <Modal
      footer={footer}
      hideCloseButton
      open={showModal}
      size={ModalSize.SMALL}
      title={title}
    >
      {acceptTermsLoad.error || onboardingLoad.error ? (
        <div>There was an error during onboarding.</div>
      ) : (
        content
      )}
    </Modal>
  );
});
